import { forwardRef } from 'react';
import DatePicker from 'react-datepicker';
import dayjs, { Dayjs } from 'dayjs';
import { Calendar, ChevronDown } from 'lucide-react';
import { cn } from './cn';
import { Dropdown } from './Dropdown';
import { POPOVER_PORTAL_ID, keepPopperOnScreen } from './popoverPortal';
import { MenuItem } from './Menu';

export interface DateRangePreset {
  label: string;
  value: [Dayjs, Dayjs];
}

export interface DateRangePickerProps {
  value: [Dayjs, Dayjs];
  onChange: (range: [Dayjs, Dayjs]) => void;
  presets?: DateRangePreset[];
  className?: string;
}

// A single-date trigger ("From 21 May 2026" / "To 20 Jun 2026"). react-datepicker
// clones this with `value` + `onClick`; the label is ours. The date reads in the app-wide
// DD MMM YYYY shape — a numeric 05-06-2026 is ambiguous between day-first and month-first.
const DateTrigger = forwardRef<HTMLButtonElement, { value?: string; onClick?: () => void; label?: string }>(
  function DateTrigger({ value, onClick, label }, ref) {
    return (
      <button
        type="button"
        ref={ref}
        onClick={onClick}
        className={cn(
          'inline-flex h-9 items-center gap-1.5 rounded-control border border-line bg-surface px-2.5 text-sm',
          'transition-colors hover:border-brand-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40',
        )}
      >
        <Calendar size={14} className="text-muted" />
        {label && <span className="type-overline text-subtle">{label}</span>}
        <span className="tabular-nums text-content">{value}</span>
      </button>
    );
  },
);

/**
 * Explicit From / To date pickers (replaces the ambiguous single range field).
 * Each is an independent single-date calendar, mutually constrained so From ≤ To.
 * Presets remain available via a compact "Quick" dropdown. Calendar visuals are
 * themed in styles/theme.css (`.react-datepicker*`).
 */
export function DateRangePicker({ value, onChange, presets = [], className }: DateRangePickerProps) {
  const [start, end] = value;

  /**
   * The preset button NAMES THE PERIOD IT IS SHOWING.
   *
   * It used to say "Quick", which describes the menu's speed rather than anything about the data —
   * a reader had to open it to find out what the dashboard was even showing. Now it reads
   * "Last 30 days" / "This month", falling back to "Custom range" when the dates match no preset,
   * so the control states the current period AND offers the others.
   */
  const activePreset = presets.find(
    (p) => p.value[0].isSame(start, 'day') && p.value[1].isSame(end, 'day'),
  )?.label;

  return (
    <div className={cn('inline-flex flex-wrap items-center gap-2', className)}>
      <DatePicker
        selected={start.toDate()}
        onChange={(d: Date | null) => d && onChange([dayjs(d), end])}
        maxDate={(end.isAfter(dayjs(), 'day') ? dayjs() : end).toDate()}
        dateFormat="dd MMM yyyy"
        customInput={<DateTrigger label="From" />}
        popperClassName="gb-datepicker-popper"
        popperModifiers={[keepPopperOnScreen]}
        // At <body>, above the sticky app header — inline, a calendar that opened upward hid its month arrows under it.
        portalId={POPOVER_PORTAL_ID}
      />
      <DatePicker
        selected={end.toDate()}
        onChange={(d: Date | null) => d && onChange([start, dayjs(d)])}
        minDate={start.toDate()}
        maxDate={dayjs().toDate()}
        dateFormat="dd MMM yyyy"
        customInput={<DateTrigger label="To" />}
        popperClassName="gb-datepicker-popper"
        popperModifiers={[keepPopperOnScreen]}
        portalId={POPOVER_PORTAL_ID}
      />

      {presets.length > 0 && (
        <Dropdown
          trigger="click"
          panel={presets.map((p) => (
            <MenuItem key={p.label} onClick={() => onChange(p.value)}>
              {p.label}
            </MenuItem>
          ))}
        >
          <button
            type="button"
            className="inline-flex h-9 items-center gap-1 rounded-control border border-line bg-surface px-2.5 text-sm text-content transition-colors hover:border-brand-400 hover:text-brand-700"
          >
            {activePreset ?? 'Custom range'}
            <ChevronDown size={14} className="text-muted" />
          </button>
        </Dropdown>
      )}
    </div>
  );
}
